import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Handshake, TrendingDown, Info, BookmarkPlus, BookmarkCheck, CheckCircle2, Clock, AlertTriangle, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import VistoVerdeWizard from './VistoVerdeWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fase 2 Task 2.2 — Configuratore canone lato CLIENTE.
 *
 * Mostra:
 *   - Canone Massimo (barrato) vs Canone Target (evidenziato) se in convenzione
 *   - Slider anticipo IVA-inclusa che ricalcola live la rata
 *   - Label risparmio "Risparmi {X}€/mese grazie alla tua convenzione {Nome}"
 *   - Scorporo IVA anticipo (imponibile + IVA)
 *
 * Task 2.4a — Aggiunte:
 *   - Salva/Rimuovi Visto Verde con mutex 1 offerta attiva
 *   - Countdown 48h invio documenti
 *   - Rata torna al Canone Massimo se timer scaduto
 *
 * Dipende da:
 *   - /api/client/quote/{vehicle_id} (motore Task 2.1)
 *   - /api/client/preventivi/save
 *   - /api/client/preventivi/{id}/visto-verde
 *   - /api/client/preventivi/{id}/conferma-documenti
 */
const EURO = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString('it-IT') : '—');
const EURO2 = (n) => (Number.isFinite(n) ? n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—');

// Debounce helper interno (evita fetch a ogni step del slider)
function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Formatta countdown "23h 45m" da timestamp ISO + 48h
export function formatCountdown(vistoVerdeAt) {
  if (!vistoVerdeAt) return '';
  try {
    const start = new Date(vistoVerdeAt);
    const end = new Date(start.getTime() + 48 * 3600 * 1000);
    const diff = end.getTime() - Date.now();
    if (diff <= 0) return 'Scaduto';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  } catch {
    return '';
  }
}

export default function ClienteQuoteCalculator({
  vehicleId,
  durata,
  km,
  initialAnticipoIvato = 0,
  offer = null,
  onOfferChanged = null,
}) {
  const { getAuthHeader, user } = useAuth();
  const is4v = Boolean(offer?.vehicle_info?.use_4vantage);
  const [anticipoIvato, setAnticipoIvato] = useState(initialAnticipoIvato || 0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [timerLabel, setTimerLabel] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const anticipoDebounced = useDebounced(anticipoIvato, 250);
  const lastReqRef = useRef(0);

  // Stato derivato dall'offer passata dal parent (I Miei Preventivi)
  const isSaved = Boolean(offer?.saved_by_client);
  const hasVistoVerde = Boolean(offer?.visto_verde);
  const documentiConfermati = Boolean(offer?.documenti_conferma_at || offer?.in_lavorazione);
  const scaduto = Boolean(offer?.visto_verde_scaduto);

  // Aggiorna countdown ogni minuto
  useEffect(() => {
    if (!hasVistoVerde || documentiConfermati || scaduto || !offer?.visto_verde_at) {
      setTimerLabel('');
      return;
    }
    setTimerLabel(formatCountdown(offer.visto_verde_at));
    const interval = setInterval(() => {
      setTimerLabel(formatCountdown(offer.visto_verde_at));
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [hasVistoVerde, documentiConfermati, scaduto, offer?.visto_verde_at]);

  const fetchQuote = useCallback(async () => {
    if (!vehicleId) return;
    const reqId = ++lastReqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/client/quote/${vehicleId}`, {
        params: { durata, km, anticipo_ivato: anticipoDebounced, use_4vantage: is4v },
        headers: getAuthHeader(),
      });
      // Scarta risposte fuori sequenza (race quando l'utente muove il slider velocemente)
      if (reqId !== lastReqRef.current) return;
      setQuote(res.data);
    } catch (err) {
      if (reqId !== lastReqRef.current) return;
      setError(err.response?.data?.detail || 'Errore nel calcolo canone');
    } finally {
      if (reqId === lastReqRef.current) setLoading(false);
    }
  }, [vehicleId, durata, km, anticipoDebounced, is4v, getAuthHeader]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  // Se timer scaduto senza conferma docs, sovra-scrive canone_applicato a canone_massimo
  const effectiveRata = scaduto && quote
    ? Math.max(0, (quote.canone_massimo || 0) - (quote.sconto_mensile_da_anticipo || 0))
    : quote?.rata_finale;

  const doAction = async (path, body) => {
    setActionLoading(true);
    try {
      const res = body !== undefined
        ? await axios.post(`${API}${path}`, body, { headers: getAuthHeader() })
        : await axios.post(`${API}${path}`, {}, { headers: getAuthHeader() });
      if (typeof onOfferChanged === 'function') await onOfferChanged();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async ({ silent = false } = {}) => {
    try {
      await doAction('/client/preventivi/save', {
        vehicle_id: vehicleId,
        durata,
        km,
        anticipo_ivato: anticipoIvato,  // 2.6.2 — persistiamo l'anticipo per PDF coerente
        offer_id: offer?.id,  // 2.6.1 — riusa l'offer esistente invece di crearne una nuova
        listino: is4v ? '4vantage' : undefined,
      });
      if (!silent) toast.success('Preventivo salvato in "I Miei Preventivi"');
    } catch {/* toast already shown */}
  };

  // 2.6.3 — Auto-persist dell'anticipo dopo che l'offerta è già stata salvata
  // (evita che l'utente perda la modifica dello slider dopo Salva).
  // Non salva mentre l'offerta è congelata (Visto Verde attivo o documenti confermati).
  const canAutoSave = isSaved && !hasVistoVerde && !documentiConfermati && !scaduto;
  const savedAnticipoRef = useRef(initialAnticipoIvato || 0);
  useEffect(() => {
    if (!canAutoSave) return;
    if (Math.abs(anticipoDebounced - savedAnticipoRef.current) < 1) return;
    savedAnticipoRef.current = anticipoDebounced;
    // Fire-and-forget: silent save
    handleSave({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anticipoDebounced, canAutoSave]);

  const handleToggleVistoVerde = async () => {
    if (!offer?.id) return;
    try {
      // 2.6.3 — Se stiamo attivando il VV, salva prima l'anticipo corrente per
      // congelare i parametri (durata/km/anticipo/canone) nell'offerta.
      if (!hasVistoVerde) {
        await handleSave({ silent: true });
      }
      const res = await doAction(`/client/preventivi/${offer.id}/visto-verde`);
      if (res.visto_verde) {
        toast.success('Visto Verde applicato — completa il percorso guidato per finalizzare la pratica');
        setWizardOpen(true);
      } else {
        toast.success('Visto Verde rimosso');
      }
    } catch { /* toast already shown */ }
  };

  const handleConfermaDoc = async () => {
    if (!offer?.id) return;
    try {
      await doAction(`/client/preventivi/${offer.id}/conferma-documenti`);
      toast.success('Documenti confermati. Il consulente prenderà in carico la pratica.');
    } catch {/* */}
  };

  if (error) {
    return (
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="quote-error">
        {error}
      </div>
    );
  }

  const hasTarget = quote?.canone_target != null && quote.canone_target !== quote.canone_massimo;
  // 2.6.20 — Canone effettivo dinamico: sottrai lo sconto mensile derivante dall'anticipo
  // così Canone Standard, Canone Target e Rata finale si ricalcolano coerentemente al variare dello slider.
  const scontoMensile = Number(quote?.sconto_mensile_da_anticipo || 0);
  const canoneMassimoEffettivo = Math.max(0, Number(quote?.canone_massimo || 0) - scontoMensile);
  const canoneTargetEffettivo = quote?.canone_target != null
    ? Math.max(0, Number(quote.canone_target) - scontoMensile)
    : null;
  const risparmioMensile = hasTarget ? (quote.canone_massimo - quote.canone_target) : 0;
  const isBaseConv = user?.convenzione_nome === 'Machenoleggio' || user?.convenzione_codice === 'MACHE-BASE' || (quote?.convenzione_nome === 'Machenoleggio');
  const convLabel = quote?.convenzione_nome
    ? (isBaseConv ? 'grazie alla registrazione Machenoleggio' : `grazie alla tua convenzione ${quote.convenzione_nome}`)
    : null;

  // Max anticipo: fino a ~40% del listino stimato (canone_base * durata)
  const anticipoMax = Math.max(5000, Math.round((quote?.canone_base || 500) * (durata || 48) * 0.4));

  return (
    <div className="mt-4 rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-4 space-y-4" data-testid="cliente-quote-calculator">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
          <TrendingDown className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Personalizza la tua rata</h4>
          <p className="text-xs text-slate-500">Muovi l&apos;anticipo per aggiornare la rata mensile.</p>
        </div>
        {is4v && (
          <span className="ml-auto text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#7B1FA2' }} data-testid="calculator-4vantage-badge">
            4Vantage
          </span>
        )}
        {loading && <Loader2 className={`w-4 h-4 animate-spin text-slate-400 ${is4v ? '' : 'ml-auto'}`} />}
      </div>

      {/* Doppio canone */}
      <div className="grid grid-cols-2 gap-3">
        <div
          className={`p-3 rounded-lg border ${hasTarget ? 'border-slate-200 bg-white opacity-70' : 'border-emerald-300 bg-emerald-50'}`}
          data-testid="canone-massimo-box"
        >
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Canone Standard</p>
          <p className={`text-lg font-bold ${hasTarget ? 'text-slate-500 line-through decoration-slate-400' : 'text-emerald-700'}`}>
            €{EURO(canoneMassimoEffettivo)}<span className="text-xs font-normal">/mese</span>
          </p>
        </div>
        {hasTarget ? (
          <div className="p-3 rounded-lg border-2 border-emerald-500 bg-emerald-50 shadow-sm" data-testid="canone-target-box">
            <p className="text-[10px] uppercase tracking-wide text-emerald-700 flex items-center gap-1">
              <Handshake className="w-3 h-3" /> Canone Target Sbloccabile
            </p>
            <p className="text-lg font-bold text-emerald-700">
              €{EURO(canoneTargetEffettivo)}<span className="text-xs font-normal">/mese</span>
            </p>
            <p className="text-[10px] text-emerald-700/80 leading-tight mt-1">
              Ottieni questo canone inviando i documenti entro <strong>48 ore</strong> dall&apos;accettazione e seguendo gli step operativi richiesti.
            </p>
          </div>
        ) : (
          <div className="p-3 rounded-lg border border-slate-200 bg-white flex items-center justify-center">
            <p className="text-xs text-slate-400 text-center">Nessun ulteriore sconto convenzione</p>
          </div>
        )}
      </div>

      {/* Label risparmio */}
      {hasTarget && risparmioMensile > 0 && convLabel && (
        <div className="p-2.5 rounded-md bg-emerald-100 border border-emerald-300 text-center" data-testid="risparmio-label">
          <p className="text-sm text-emerald-800">
            Risparmi <strong>€{EURO(risparmioMensile)}/mese</strong> {convLabel}
          </p>
        </div>
      )}

      {/* Slider anticipo */}
      <div className="pt-3 border-t border-slate-100">
        <div className="flex flex-col items-center text-center mb-3">
          <label className="text-xs font-medium text-slate-700 flex items-center gap-2 flex-wrap justify-center">
            <span>Anticipo (IVA inclusa)</span>
            <span className="text-[10px] font-normal italic text-slate-500">— Muovi il cursore per cambiarlo</span>
            {(hasVistoVerde || documentiConfermati) && (
              <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">
                Congelato
              </span>
            )}
          </label>
          <input
            type="number"
            min="0"
            max={anticipoMax}
            step="100"
            value={anticipoIvato}
            onChange={(e) => setAnticipoIvato(Math.max(0, Math.min(anticipoMax, Number(e.target.value) || 0)))}
            disabled={hasVistoVerde || documentiConfermati}
            className="mt-2 w-40 h-11 px-3 text-2xl font-bold text-center text-emerald-700 bg-emerald-50 border-2 border-emerald-300 rounded-lg disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed shadow-sm"
            data-testid="anticipo-input"
          />
        </div>
        <input
          type="range"
          min="0"
          max={anticipoMax}
          step="100"
          value={anticipoIvato}
          onChange={(e) => setAnticipoIvato(Number(e.target.value))}
          disabled={hasVistoVerde || documentiConfermati}
          className="w-full accent-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="anticipo-slider"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>€0</span>
          <span>€{EURO(anticipoMax / 2)}</span>
          <span>€{EURO(anticipoMax)}</span>
        </div>
        {(hasVistoVerde || documentiConfermati) && (
          <p className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="parametri-congelati-note">
            I parametri di questa offerta sono stati bloccati con l&apos;attivazione del Visto Verde. Per modificarli, rimuovi prima il Visto Verde.
          </p>
        )}
      </div>

      {/* 2.6.19 — Franchigie assicurative fisse (non modificabili in area cliente) */}
      <div className="mt-3 p-2.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="franchigie-row">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
          Franchigie assicurative
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-slate-800">
          <span>RCA <span className="text-emerald-700">€250</span></span>
          <span className="text-slate-300">·</span>
          <span>Danni/Kasco <span className="text-emerald-700">{is4v ? '€500' : '€1.000'}</span></span>
          <span className="text-slate-300">·</span>
          <span>Furto <span className="text-emerald-700">{is4v ? '0%' : '10%'}</span></span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
          Le franchigie non sono modificabili qui. Per personalizzarle o aggiungere servizi extra
          (auto sostitutiva, gomme invernali/estive) contatta il tuo agente di riferimento.
        </p>
      </div>

      {/* Scorporo IVA + rata finale */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-emerald-100">
        <div>
          <p className="text-[10px] uppercase text-slate-500">Imponibile</p>
          <p className="text-sm font-semibold text-slate-800">€{EURO2(quote?.anticipo_imponibile || 0)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500">IVA {Math.round(quote?.iva_anticipo_pct || 22)}%</p>
          <p className="text-sm font-semibold text-slate-800">€{EURO2(quote?.iva_anticipo || 0)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500">Sconto/mese</p>
          <p className="text-sm font-semibold text-emerald-700">-€{EURO2(quote?.sconto_mensile_da_anticipo || 0)}</p>
        </div>
      </div>

      {/* Rata finale */}
      <div className="p-3 rounded-lg bg-slate-900 text-white flex items-center justify-between" data-testid="rata-finale-box">
        <div>
          <p className="text-[10px] uppercase text-slate-300">Rata finale mensile</p>
          <p className="text-2xl font-bold">€{EURO2(effectiveRata || 0)}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase text-slate-400">Durata</p>
          <p className="text-sm font-semibold">{quote?.durata_mesi || durata} mesi</p>
        </div>
      </div>

      {scaduto && (
        <div className="p-2.5 rounded-md bg-amber-100 border border-amber-300 flex items-center gap-2" data-testid="scaduto-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <span className="text-xs text-amber-900">Tempo per sconto extra scaduto. Contatta il consulente per lo sblocco manuale.</span>
        </div>
      )}

      {/* Azioni cliente: Salva / Visto Verde / Conferma Documenti */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-emerald-100">
        {!isSaved && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={actionLoading}
            onClick={handleSave}
            data-testid="btn-save-preventivo"
          >
            <BookmarkPlus className="w-4 h-4" /> Salva tra i miei preventivi
          </Button>
        )}
        {isSaved && !hasVistoVerde && !documentiConfermati && (
          <div className="flex flex-col items-start gap-1">
            <Button
              size="sm"
              className="gap-1 bg-emerald-600 hover:bg-emerald-700"
              disabled={actionLoading || scaduto}
              onClick={handleToggleVistoVerde}
              data-testid="btn-visto-verde"
            >
              <CheckCircle2 className="w-4 h-4" /> Blocca offerta e richiedi fattibilità
            </Button>
            <span className="text-[10px] text-slate-500 leading-tight">
              Non vincolante • Avvia la verifica dei requisiti di credito
            </span>
          </div>
        )}
        {isSaved && hasVistoVerde && !documentiConfermati && !scaduto && (
          <>
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold" data-testid="timer-badge">
              <Clock className="w-3.5 h-3.5" /> {timerLabel || '48h'} rimanenti
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={actionLoading}
              onClick={() => setWizardOpen(true)}
              data-testid="btn-riprendi-wizard"
            >
              Riprendi percorso guidato
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={actionLoading}
              onClick={handleToggleVistoVerde}
              data-testid="btn-remove-visto-verde"
            >
              Rimuovi Visto Verde
            </Button>
            <Button
              size="sm"
              className="gap-1 bg-slate-900 hover:bg-slate-800"
              disabled={actionLoading}
              onClick={handleConfermaDoc}
              data-testid="btn-conferma-documenti"
            >
              <Send className="w-4 h-4" /> Ho inviato i documenti
            </Button>
          </>
        )}
        {documentiConfermati && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold" data-testid="in-lavorazione-badge">
            <BookmarkCheck className="w-4 h-4" /> In lavorazione — il consulente ti aggiornerà a breve
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-[11px] text-slate-500">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <p>
          Tutti gli importi mensili sono IVA esclusa. Lo scorporo IVA riguarda esclusivamente
          l&apos;anticipo che decurta la quota capitale del contratto.
        </p>
      </div>

      <VistoVerdeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        offer={offer}
        onCompleted={() => onOfferChanged && onOfferChanged()}
      />
    </div>
  );
}
